import { useState } from 'react';
import {
  getQuiz,
  getScore,
  getScoreEvents,
  submitQuiz,
  type ScoreEventRead,
} from '../api/endpoints.js';
import { useAction, useApi } from '../api/hooks.js';
import { signedDelta } from '../flow/protection.js';
import { quizQuestionApplies as applies } from '../flow/quiz-gate.js';
import { Async, Badge, Bool, Button, Failure, KeyValue, Panel, Raw, Screen } from '../ui/kit.js';

type Answers = Record<string, string | string[]>;

function toggle(list: string[], option: string): string[] {
  return list.includes(option) ? list.filter((item) => item !== option) : [...list, option];
}

export function QuizScreen() {
  const quiz = useApi(getQuiz, []);
  const score = useApi(getScore, []);
  const [answers, setAnswers] = useState<Answers>({});
  const submit = useAction(submitQuiz, () => score.reload());

  // GET /v1/me/score/events, paged: the first page loads with the screen, and
  // "load more" appends — the api's own cursor decides when there is none.
  const eventsPage = useApi(() => getScoreEvents(), []);
  const [moreEvents, setMoreEvents] = useState<ScoreEventRead[]>([]);
  const loadMore = useAction(getScoreEvents, (result) => {
    setMoreEvents((previous) => [...previous, ...result.events]);
  });
  const events = [...(eventsPage.data?.events ?? []), ...moreEvents];
  const nextCursor =
    moreEvents.length > 0 ? (loadMore.result?.next_cursor ?? null) : (eventsPage.data?.next_cursor ?? null);

  return (
    <Screen
      title="Quiz & score"
      lede="The definition is data — options and their scoring live in profile.quiz_definitions, so this form is rendered from whatever the api serves. The score itself is the ONE Likeness Health Score v0.3, computed once by score/service.ts and served verbatim — this screen renders exactly what it returns."
    >
      <Panel title="current score">
        {score.error !== null && score.error.code === 'NO_QUIZ_RESPONSE' ? (
          <p className="empty">
            no quiz response yet — this is the one read allowed to 404, and it does so with a code
            the client routes on
          </p>
        ) : score.error !== null && score.error.code === 'QUIZ_OUTDATED' ? (
          <p className="empty">quiz taken against a retired version — take it again</p>
        ) : (
          <Async state={score}>
            {(read) => {
              // The api nests the score under `score` alongside `scope_note`
              // — a number with no stated scope reads as a safety claim it
              // never makes.
              const data = read.score;
              return (
                <>
                  <KeyValue
                    rows={[
                      ['live', <strong key="live">{data.live}</strong>],
                      // Server-labelled, noun included ("low risk") — rendered
                      // verbatim, never re-derived from `live`.
                      ['band', <Badge key="band">{data.band}</Badge>],
                      ['baseline', data.baseline],
                      ['recovered', data.recovered],
                      ['escrow_released', data.escrow_released],
                      ['dynamic_deduction', data.dynamic_deduction],
                      ['current_ceiling', data.current_ceiling],
                      ['maximum_ceiling', data.maximum_ceiling],
                      ['scoring_version', data.scoring_version],
                      ['quiz_version', data.quiz_version],
                      ['computed_at', data.computed_at],
                    ]}
                  />
                  <p className="muted">{read.scope_note}</p>
                  {data.breakdown.quiz.length > 0 && (
                    <div className="scroll-x">
                      <table>
                        <thead>
                          <tr>
                            <th>key</th>
                            <th>value</th>
                            <th>deduction</th>
                            <th>type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.breakdown.quiz.map((entry) => (
                            <tr key={entry.key}>
                              <td>{entry.key}</td>
                              <td>{entry.value}</td>
                              <td>{entry.deduction}</td>
                              <td>{entry.type}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {data.breakdown.dynamic.length > 0 && (
                    <div className="scroll-x">
                      <table>
                        <thead>
                          <tr>
                            <th>id</th>
                            <th>active</th>
                            <th>points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.breakdown.dynamic.map((entry) => (
                            <tr key={entry.id}>
                              <td>{entry.id}</td>
                              <td>
                                <Bool value={entry.active} />
                              </td>
                              <td>{entry.points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Raw value={read} />
                </>
              );
            }}
          </Async>
        )}
      </Panel>

      <Panel title="score events">
        <Async state={eventsPage}>
          {() => (
            <>
              {events.length === 0 ? (
                <p className="empty">no score movements yet — every change lands here</p>
              ) : (
                <div className="scroll-x">
                  <table>
                    <thead>
                      <tr>
                        <th>when</th>
                        <th>trigger</th>
                        <th>component</th>
                        <th>delta</th>
                        <th>before</th>
                        <th>after</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.event_id}>
                          <td>{new Date(event.created_at).toLocaleString()}</td>
                          <td>{event.trigger}</td>
                          <td>{event.component}</td>
                          <td>
                            <code>{signedDelta(event.delta)}</code>
                          </td>
                          <td>{event.before ?? '—'}</td>
                          <td>{event.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {nextCursor !== null && (
                <Button onClick={() => loadMore.run(nextCursor)} disabled={loadMore.pending}>
                  {loadMore.pending ? 'loading…' : 'load more'}
                </Button>
              )}
              {loadMore.error !== null && <Failure error={loadMore.error} />}
              <Raw label="GET /v1/me/score/events" value={eventsPage.data} />
            </>
          )}
        </Async>
      </Panel>

      <Async state={quiz}>
        {(data) => (
          <Panel title={`questions · ${data.quiz_version}`}>
            {data.questions.filter((question) => applies(question, answers)).map((question) => {
              const value = answers[question.key];
              const multi = question.multi === true;
              const selected = multi
                ? Array.isArray(value)
                  ? value
                  : []
                : typeof value === 'string'
                  ? value
                  : '';
              return (
                <div key={question.key} style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}>
                    {question.prompt}{' '}
                    {!question.scored && <span className="muted">(unscored)</span>}
                  </div>
                  {multi ? (
                    <div className="row">
                      {question.options.map((option) => (
                        <label key={option} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={(selected as string[]).includes(option)}
                            onChange={() =>
                              setAnswers({
                                ...answers,
                                [question.key]: toggle(selected as string[], option),
                              })
                            }
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select
                      value={selected as string}
                      onChange={(event) =>
                        setAnswers({ ...answers, [question.key]: event.target.value })
                      }
                    >
                      <option value="">—</option>
                      {question.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}

            <Button
              onClick={() => submit.run(data.quiz_version, answers)}
              disabled={submit.pending}
            >
              {submit.pending ? 'submitting…' : 'Submit answers'}
            </Button>
            {submit.error !== null && (
              <Failure
                error={submit.error}
                hint={
                  {
                    UNRECOGNISED_ANSWER:
                      'An answer key or value is not in the active definition — the service validates against the definition row, which zod cannot do statically.',
                    QUIZ_VERSION_INACTIVE: 'This definition is no longer active; reload the quiz.',
                  }[submit.error.code]
                }
              />
            )}
            {submit.result !== null && submit.result.score === null && (
              <p className="empty">
                answers saved — the score recompute failed and will be repaired on the next read
              </p>
            )}
            {submit.result !== null && <Raw label="POST /v1/quiz/responses" value={submit.result} />}
            <Raw label="GET /v1/quiz" value={data} />
          </Panel>
        )}
      </Async>
    </Screen>
  );
}
